import React, { useEffect, useState } from "react";
import { Sun, Wind, Zap } from "lucide-react";
import SolarPanelBg from "../assets/banner_2.webp";
import BlackLogo from "../assets/ecositeAsset.png";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Pie from "../assets/pie.png";
import Line from "../assets/line.png";
import Bar from "../assets/bar.png";
import Portal from "../assets/portal.png";
import CustomModal from "./CustomModal";
import { FaTwitter, FaLinkedin } from "react-icons/fa";
import CountUp from "react-countup";

const charts = [Portal, Pie, Line, Bar];

const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.pageYOffset);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return -(offset * speed);
};

const SplashPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const parallaxOffset = useParallax(0.5);
  const settings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  useEffect(() => {
    const imageUrls = [SolarPanelBg, BlackLogo];
    let loadedImages = 0;

    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        loadedImages += 1;
        if (loadedImages === imageUrls.length) {
          setImagesLoaded(true);
        }
      };
    });
  }, []);

  if (!imagesLoaded) {
    return null;
  }

  return (
    <div className="bg-green-50 min-h-screen">
      {/* Header */}
      <header className="bg-[#2d9376] shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <img src={BlackLogo} alt="Logo" className="h-8 w-26" />
          <nav>
            <ul className="flex space-x-6">
              <li>
                <a
                  href="#"
                  className="text-black hover:bg-[#2d9376] transition duration-300"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="text-black hover:text-green-400 transition duration-300"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="/login"
                  className="text-black hover:text-green-400 transition duration-300"
                >
                  Portal
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-black hover:text-green-400 font-bold transition duration-300"
                  onClick={openModal}
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Contact Modal */}
      <CustomModal isOpen={isModalOpen} onRequestClose={closeModal} />

      {/* Hero Section with Parallax Effect */}
      <section
        className="relative bg-cover bg-center h-screen overflow-hidden"
        style={{ backgroundImage: `url(${SolarPanelBg})` }}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          style={{ transform: `translateY(${parallaxOffset}px)` }}
        ></div>
        <div className="container mx-auto px-4 h-full flex items-center relative z-10">
          <div
            className="text-white max-w-3xl"
            style={{ transform: `translateY(${-parallaxOffset * 0.3}px)` }}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Accelerating UK commercial solar
            </h1>
            <p className="text-xl md:text-2xl mb-10 leading-relaxed">
              We find and pre-qualify new sites for commercial solar, connecting
              consultants, installer/operators, and financiers to end clients.
            </p>
            <div className="space-x-6">
              <button
                className="bg-[#2d9376] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#2d9376] transition duration-300 transform hover:scale-105"
                onClick={openModal}
              >
                Contact Us
              </button>
              <button className="bg-transparent text-white px-8 py-4 rounded-full text-lg font-semibold border-2 border-white hover:bg-[#2d9376] transition duration-300 transform hover:scale-105">
                <a href="/about">Learn More</a>
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="container mx-auto px-4 py-12">
        <div className="text-left mb-12">
          <h2 className="text-3xl font-bold mb-4">What We Do</h2>
          <p className="text-xl text-gray-600">
            We use AI to bring together all the parties for new commercial solar
            systems, anticipating their information needs.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex items-center">
            <div className="bg-green-600 text-white p-2 rounded-full mr-4">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Objective</h3>
              <p className="text-gray-600">
                Our objective is to assist the UK to meet its net-zero energy
                goals by accelerating the UK’s commercial solar sector.
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <div className="bg-green-600 text-white p-2 rounded-full mr-4">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">WattSearch</h3>
              <p className="text-gray-600">
                Our WattSearch software uses AI to identify and assess
                unrealised commercial solar sites.
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <div className="bg-green-600 text-white p-2 rounded-full mr-4">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Quantify</h3>
              <p className="text-gray-600">
                We quantify the cost and carbon savings available to site owners
                and make it as easy as possible to realise them.
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <div className="bg-green-600 text-white p-2 rounded-full mr-4">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Connect</h3>
              <p className="text-gray-600">
                We connect consultants, installer/operators and financiers to
                these new sites.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Power Section */}
      <section className="bg-[#2d9376] text-white py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
            Power that Doesn't Cost the Earth
          </h2>
          <p className="text-xl mb-10 max-w-2xl leading-relaxed">
            If you want clean power, we've got it! Our solution allows you to
            harness nature's energy to support a low-carbon future, without
            compromising on efficiency or reliability.
          </p>
          <button className="bg-white text-green-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition duration-300 transform hover:scale-105">
            <a
              href="https://calendly.com/contact-ecosite"
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule a call today
            </a>
          </button>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-6">Metrics that matter to us:</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <CountUp
              end={15000}
              duration={5}
              suffix=" +"
              className="text-4xl font-bold"
            />
            <p className="text-gray-600">Identified Sites</p>
          </div>
          <div>
            <CountUp
              end={1800000}
              duration={5}
              suffix=" kWh+"
              className="text-4xl font-bold"
            />
            <p className="text-gray-600">Total Generative Power</p>
          </div>
          <div>
	  {/*
            <CountUp
              end={400}
              duration={5}
              suffix=" +"
              className="text-4xl font-bold"
            />
	    */}
	  <p className="text-3xl font-bold">Coming soon Spring 2025</p>
            <p className="text-gray-600">Active Installations</p>
          </div>
        </div>
      </section>
{/*
      <section className="bg-gray-100 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6">
            Insights that will help you connect with your prospect better
          </h2>
          <p className="mb-6">
            In our trial version we provide you with the following insights to
            know your prospects in area of your interest better.
          </p>
          <div className="w-200 h-100 mx-auto">
            <Slider {...settings}>
              {charts.map((src, index) => (
                <div key={index}>
                  <img
                    src={src}
                    alt={`Slide ${index + 1}`}
                    className="rounded-lg shadow-lg w-full"
                  />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>
*/}
      {/* Values Section */}
      <section className="bg-green-50 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6">Our values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Sun className="h-12 w-12 text-green-600" />,
                title: "Customer Care",
                content:
                  "We are committed to providing the best customer service to our clients.",
              },
              {
                icon: <Wind className="h-12 w-12 text-green-600" />,
                title: "Transparency",
                content:
                  "We believe in being transparent and honest in all our dealings.",
              },
              {
                icon: <Zap className="h-12 w-12 text-green-600" />,
                title: "Sustainability",
                content:
                  "We are committed to sustainability and reducing our carbon footprint, and that of our clients.",
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg hover:transform hover:scale-105 transition duration-300"
              >
                {value.icon}
                <h3 className="text-xl font-semibold my-2">{value.title}</h3>
                <p className="text-gray-600">{value.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
	<section className="bg-white py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6 text-center">
            Ecosite is grant funded by UKSPF in partnership with BCP Council
          </h2>
          </div>
        </section>


{/*
      <section className="bg-white py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6 text-center">
            What they say about Ecosite
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                avatar: "https://ui-avatars.com/api/?name=John+Doe",
                name: "John Doe",
                title: "CEO, Company",
                testimonial:
                  "This service is fantastic! It has changed the way we do business.",
              },
              {
                avatar: "https://ui-avatars.com/api/?name=Jane+Smith",
                name: "Jane Smith",
                title: "Marketing Director",
                testimonial:
                  "Highly recommend this to anyone looking to improve their workflow.",
              },
              {
                avatar: "https://ui-avatars.com/api/?name=Sam+Wilson",
                name: "Sam Wilson",
                title: "Product Manager",
                testimonial:
                  "A game-changer in our industry. Exceptional quality and support.",
              },
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-100 p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="h-12 w-12 rounded-full mr-4"
                  />
                  <div>
                    <h3 className="text-xl font-semibold">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-600">{testimonial.title}</p>
                  </div>
                </div>
                <p className="text-gray-700">{testimonial.testimonial}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
	  */}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">About</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/about" className="hover:text-green-400">
                    Our Story
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                {/* <a
                  href="https://x.com/ecositeltd"
                  target="_blank"
                  className="text-xl hover:text-green-400"
                >
                  <FaTwitter />
                </a> */}
                <a
                  href="https://www.linkedin.com/company/ecosite-ltd/"
                  target="_blank"
                  className="text-xl hover:text-green-400"
                >
                  <FaLinkedin />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center">
            <p>&copy; 2024 Ecosite. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashPage;
